import { Badge } from 'cpool-components/general/Badge';
import { useTranslation } from 'react-i18next';
import { LuUsersRound } from 'react-icons/lu';

import { CountBadge } from '../CountBadge';
import { RepresentationItem } from '~/components/Header/menu/representation/RepresentationItem';
import Spinner from '~/components/icons/Spinner';
import { IconBadge } from '~/features/dashboard/components/IconBadge';
import { useRepresentedUsers } from '~/hooks/useRepresentedUser';
import {
    hasActiveRepresentationNow,
    isAbsenceFuture,
    sortRepresentedUsersByActiveAbsence,
} from '~/utils/representation';

type Props = {
    userId: string;
};

export const Representation: React.FC<Props> = ({ userId }) => {
    const { t } = useTranslation();

    const { data, isError, isLoading } = useRepresentedUsers(userId);

    if (!isError && (isLoading || !data)) {
        return (
            <div className="w-full inset-0 flex items-center justify-center z-50">
                <Spinner />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="py-2.5 px-5">
                <span>{t('DASHBOARD.ERROR')}</span> <br />
                <span>{t('DASHBOARD.RELOAD')}</span>
            </div>
        );
    }

    const nowMs = Date.now();
    const sortedData = sortRepresentedUsersByActiveAbsence(data, nowMs);
    const hasActiveRepresentation = data.some((item) => hasActiveRepresentationNow(item, nowMs));
    const showPlannedBadge =
        !hasActiveRepresentation && data.some((item) => item.absence?.some((a) => isAbsenceFuture(a, nowMs)));
    const representationCount = data.length;

    return (
        <section className="py-2.5 px-5 h-full">
            <div className="flex items-center justify-between pb-2 border-b-2 border-white min-h-[38px]">
                <div className="flex items-center gap-4">
                    <IconBadge icon={<LuUsersRound />} className="text-blue-text bg-blue-cloud" />
                    <h6 className="text-lg font-medium text-midnight">
                        {t('DASHBOARD.REPRESENTATION.TITLE', { count: data.length })}
                    </h6>
                    {hasActiveRepresentation && <Badge color="green">{t('DASHBOARD.REPRESENTATION.ACTIVE')}</Badge>}
                    {showPlannedBadge && (
                        <Badge className="bg-status-purple-bg border-status-purple-border text-status-purple-text">
                            {t('DASHBOARD.REPRESENTATION.PLANNED')}
                        </Badge>
                    )}
                </div>
                {representationCount > 0 && (
                    <CountBadge count={representationCount} className="text-blue-text bg-blue-cloud" />
                )}
            </div>
            {representationCount > 0 ? (
                <div className="overflow-y-auto max-h-60 scrollbar-thin scrollbar-thumb-rounded-md scrollbar-thumb-dark scrollbar-track-grey-stroke scrollbar-track-rounded-md">
                    <div className="py-3 flex flex-col gap-2">
                        {sortedData.map((item, index) => {
                            return <RepresentationItem key={index} data={item} />;
                        })}
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-center h-[calc(100%-38px)]">
                    <p className="text-sm text-midnight">
                        {t('DASHBOARD.REPRESENTATION.NO_REPRESENTATION_DESCRIPTION')}
                    </p>
                </div>
            )}
        </section>
    );
};
